/**
 * The hero band's model: the four figures a VP of Student Success checks
 * first, each with the denominator that makes it a reading rather than a
 * claim.
 *
 * ── NO DELTAS, ON PURPOSE ────────────────────────────────────────────────
 * The member app's hero carries "▲ 18% YoY" pills. This one carries a
 * denominator line instead, because every tenant on this surface is in its
 * first term and there is no prior period to compare with. A pill that says
 * "up" against a period that does not exist is exactly the kind of figure
 * this dashboard was rebuilt to stop shipping. The previous-term comparison
 * turns on in the header when a second term exists, and deltas can follow it.
 */

import type { Provenance } from "~/app/components/primitives";
import type { CohortView } from "~/app/lib/demo-data";
import { formatCount, formatPercent } from "~/app/lib/format";
import type { Cell } from "~/lib/suppression";

/** The denominator line under a figure P3 does not collect per institution yet. */
export const NOT_MEASURED_NOTE = "Not measured per institution yet";

export interface HeroKpi {
  readonly label: string;
  readonly cell: Cell<number>;
  /** The denominator line under the figure. Never empty. */
  readonly note: string;
  readonly provenance: Provenance;
}

/**
 * The last word of the institution's name takes the accent, mirroring the
 * member app's "Good afternoon, <accent>Renée</accent>" and the Data Matrix's
 * "Data <accent>Matrix</accent>". One accent word per heading, never two.
 */
export function splitAccent(name: string): { head: string; accent: string } {
  const trimmed = name.trim();
  const at = trimmed.lastIndexOf(" ");
  if (at === -1) return { head: "", accent: trimmed };
  return { head: trimmed.slice(0, at + 1), accent: trimmed.slice(at + 1) };
}

export function heroKpis(view: CohortView): readonly HeroKpi[] {
  const { seat, milestones, activity } = view;

  // Under a cohort or a slice (Sep 18 2026) the first figure is the cut's
  // count, not the institution's claims, so the tile is named for the cut and
  // its line is the cut over the band. The sample's seat meter is the whole
  // roster's, and 42 students in a cohort are not 47% of a 300 band. The
  // whole institution keeps the seat meter's own fraction.
  const cut = view.cohortLabel !== undefined || view.sliceLabel !== undefined;
  const claimedLabel = view.cohortLabel !== undefined
    ? "Students in this cohort"
    : view.sliceLabel ?? "Students claimed";
  const claimedFraction = cut
    ? view.claimed.kind === "value" && seat.purchased > 0
      ? Math.min(view.claimed.value / seat.purchased, 1)
      : null
    : seat.fraction;
  const claimedNote =
    seat.purchased > 0
      ? claimedFraction !== null
        ? `${formatPercent(claimedFraction)} of your ${formatCount(seat.purchased)} student band`
        : "Published at ten students"
      : "No band on the plan yet";

  const matchedNote =
    view.matchedRate.kind === "value"
      ? `${formatPercent(view.matchedRate.value.rate)} of claimed students`
      : "Published at ten matched students";

  // A live tenant's milestones are not counted while the reader is unbound,
  // nor its applications while the analytics are not configured
  // (live-view.ts); a refresh that could not read them says that instead. Either note replaces
  // a "none yet" that would read as a measurement of nothing. A published
  // total means ten or more students stand behind it, even when their count
  // is withheld because the students it leaves out are fewer than ten.
  const milestoneNote = milestones.notMeasured
    ? NOT_MEASURED_NOTE
    : milestones.unread
      ? "Not read on the last refresh"
      : milestones.students.kind === "value" && milestones.studentsRaw > 0
        ? `by ${formatCount(milestones.studentsRaw)} students`
        : milestones.completed.kind === "value" && milestones.completedRaw > 0
          ? "by ten or more students"
          : milestones.completed.kind === "value"
            ? `None marked complete ${milestones.span}`
            : "Published at ten completing students";

  // A live tenant counts applications from the app's analytics and not the
  // opens (Sep 11 2026), so its note says where the figure comes from rather
  // than "from N opened"; an institution the analytics hold no student for
  // is not measured, never "none".
  const opened = activity.opportunitiesOpened.total;
  const applied = activity.opportunitiesApplied.total;
  const appliedNote = activity.notMeasured
    ? NOT_MEASURED_NOTE
    : activity.appUse.unread
      ? "Not read on the last refresh"
      : opened.kind === "value" && applied.kind === "value"
        ? `from ${formatCount(opened.value)} opened`
        : applied.kind === "value" && applied.value > 0
          ? `Through the app, ${activity.appUse.span}`
          : applied.kind === "value"
            ? "No applications in this window yet"
            : applied.kind === "unavailable"
              ? NOT_MEASURED_NOTE
              : "Published at ten applying students";

  return [
    {
      label: claimedLabel,
      cell: view.claimed,
      note: claimedNote,
      provenance: view.claimedProvenance,
    },
    {
      label: "With a mentor",
      cell: view.matchedCount,
      note: matchedNote,
      provenance: view.matchedProvenance,
    },
    {
      label: "Milestones completed",
      cell: milestones.completed,
      note: milestoneNote,
      provenance: milestones.provenance,
    },
    {
      label: "Opportunities applied to",
      cell: activity.opportunitiesApplied.total,
      note: appliedNote,
      provenance: activity.provenance,
    },
  ];
}
